'use client';

/**
 * Shared before/after comparison UI for every flow that rewrites a resume.
 *
 * This markup used to live inline in `ResumeSelector`, which is why only the
 * refine flow ever showed a comparison — improve and analysis rewrite content
 * too, but had nowhere to render "here is what I changed to your resume".
 *
 * Three surfaces, one body:
 *  - `ResumeComparisonPanel`  — the collapsible card the refine flow already
 *    shows inside its own progress dialog. Extracted verbatim.
 *  - `ResumeComparisonDialog` — the same body as a standalone dialog, for flows
 *    that finish outside a dialog of their own.
 *  - `ResumeComparisonChangeList` — the list on its own, for callers that
 *    already own the surrounding chrome.
 *
 * The component is deliberately presentational: it takes a finished change list
 * plus the loading/error state around it, and never fetches. Callers differ too
 * much about where the two documents come from (two persisted ids, an unsaved
 * draft, a single rewritten field) for a fetch inside here to fit any of them.
 * See `src/lib/resume/compare.ts` for the diff that produces the list.
 */

import { type ReactNode } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/forms/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
} from '@/components/ui/layout/dialog';
import { DialogHeading } from '@/components/ui/design-system';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/layout/collapsible';
import { cn } from '@/lib/utils';
import { RESUME_COMPARE_SECTION_LABELS, type ResumeCompareSection } from '@/lib/resume/compare';
import { diffWords } from '@/lib/resume/word-diff';

export interface ResumeComparisonChange {
  id: string;
  /** A `ResumeCompareSection` key when known; used only for grouping. */
  section?: string;
  label: string;
  before?: string;
  after?: string;
}

interface ResumeComparisonStateProps {
  changes: ResumeComparisonChange[];
  isLoading?: boolean;
  error?: string | null;
  /**
   * False while the comparison has not been requested yet, so the idle prompt
   * ("click X to compare") can be told apart from a finished empty result.
   * Flows that compute their changes eagerly leave this at its default.
   */
  hasLoaded?: boolean;
  loadingMessage?: ReactNode;
  idleMessage?: ReactNode;
  emptyMessage?: ReactNode;
  /** Group the list under section headings. Off by default: a short list reads
   *  better flat, and headings would repeat the label of a single change. */
  groupBySection?: boolean;
}

function getSectionLabel(section: string | undefined): string {
  if (!section) return 'Other changes';
  return (
    RESUME_COMPARE_SECTION_LABELS[section as ResumeCompareSection] ?? 'Other changes'
  );
}

/**
 * Word-level before/after rendering for one change.
 *
 * This used to print two paragraphs, "Before: …" and "After: …". For an AI
 * rewrite the two are usually ~90% identical, so the reader had to find the
 * difference by eye — the one job the comparison is supposed to do for them.
 * Removed words are struck through in red, added words highlighted in green,
 * shared words left plain, so the change reads at a glance.
 *
 * When only one side exists the row is a pure addition or deletion, and the
 * whole value takes that treatment rather than being diffed against nothing.
 */
function ResumeChangeDiff({ before, after }: { before?: string; after?: string }) {
  const hasBefore = Boolean(before && before.trim());
  const hasAfter = Boolean(after && after.trim());

  if (!hasBefore && !hasAfter) return null;

  if (!hasBefore) {
    return (
      <p className="mt-1 rounded bg-emerald-50 px-1.5 py-1 text-emerald-900">{after}</p>
    );
  }

  if (!hasAfter) {
    return (
      <p className="mt-1 rounded bg-destructive/10 px-1.5 py-1 text-destructive line-through">
        {before}
      </p>
    );
  }

  const tokens = diffWords(before ?? '', after ?? '');

  // Loose matching means two texts can differ only by case or punctuation and
  // still produce no add/remove runs. Falling through to the diff view would
  // render something that looks unchanged, so say what actually happened.
  if (!tokens.some((token) => token.op !== 'equal')) {
    return (
      <p className="mt-1 text-muted-foreground">
        Formatting only — the wording is unchanged.
      </p>
    );
  }

  return (
    <p className="mt-1 leading-relaxed">
      {tokens.map((token, index) => {
        if (token.op === 'equal') {
          return <span key={index}>{token.value}</span>;
        }
        if (token.op === 'removed') {
          return (
            <span
              key={index}
              className="rounded-sm bg-destructive/10 text-destructive line-through decoration-destructive/60"
            >
              {token.value}
            </span>
          );
        }
        return (
          <span key={index} className="rounded-sm bg-emerald-50 font-medium text-emerald-900">
            {token.value}
          </span>
        );
      })}
    </p>
  );
}

export function ResumeComparisonChangeList({
  changes,
  groupBySection = false,
  className,
}: {
  changes: ResumeComparisonChange[];
  groupBySection?: boolean;
  className?: string;
}) {
  if (!groupBySection) {
    return (
      <ul className={cn('space-y-2 text-xs text-muted-foreground', className)}>
        {changes.map((change) => (
          <li key={change.id} className="rounded border border-border bg-background px-3 py-2">
            <p className="font-medium text-foreground">{change.label}</p>
            <ResumeChangeDiff before={change.before} after={change.after} />
          </li>
        ))}
      </ul>
    );
  }

  // Preserve first-seen order so the grouped view keeps the diff's own
  // ordering (profile first, then skills, roles, and so on).
  const groups: Array<{ section: string; changes: ResumeComparisonChange[] }> = [];
  for (const change of changes) {
    const key = change.section ?? '';
    const group = groups.find((candidate) => candidate.section === key);
    if (group) {
      group.changes.push(change);
      continue;
    }
    groups.push({ section: key, changes: [change] });
  }

  return (
    <div className={cn('space-y-4', className)}>
      {groups.map((group) => (
        <div key={group.section || 'other'} className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {getSectionLabel(group.section)}
          </p>
          <ResumeComparisonChangeList changes={group.changes} />
        </div>
      ))}
    </div>
  );
}

/** The loading / error / idle / empty / list state machine, without chrome. */
export function ResumeComparisonBody({
  changes,
  isLoading = false,
  error = null,
  hasLoaded = true,
  loadingMessage = 'Comparing versions...',
  idleMessage,
  emptyMessage = 'No content differences detected.',
  groupBySection = false,
}: ResumeComparisonStateProps) {
  if (isLoading) {
    return (
      <p className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        {loadingMessage}
      </p>
    );
  }

  if (error) {
    return <p className="text-destructive">{error}</p>;
  }

  if (!hasLoaded) {
    return <p className="text-muted-foreground">{idleMessage}</p>;
  }

  if (changes.length === 0) {
    return <p className="text-muted-foreground">{emptyMessage}</p>;
  }

  return <ResumeComparisonChangeList changes={changes} groupBySection={groupBySection} />;
}

/**
 * Collapsible "What changed" card, for flows that already own a dialog and want
 * the comparison inside it.
 */
export function ResumeComparisonPanel({
  open,
  onOpenChange,
  title = 'What changed',
  className,
  ...state
}: ResumeComparisonStateProps & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'space-y-2 rounded-md border border-border bg-muted/20 px-4 py-3 text-sm',
        className
      )}
    >
      <Collapsible open={open} onOpenChange={onOpenChange}>
        <div className="flex items-center justify-between gap-2">
          <p className="font-medium text-foreground">{title}</p>
          <CollapsibleTrigger asChild>
            <Button type="button" variant="outline" size="sm" className="h-8 px-3">
              {open ? 'Collapse' : 'Expand'}
              <ChevronDown
                className={cn('ml-1 h-4 w-4 transition-transform', open && 'rotate-180')}
              />
            </Button>
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent className="mt-2">
          <div className="max-h-72 overflow-y-auto pr-1">
            <ResumeComparisonBody {...state} />
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

/** Standalone comparison dialog, for flows that finish without one of their own. */
export function ResumeComparisonDialog({
  open,
  onOpenChange,
  title = 'What changed',
  description,
  closeLabel = 'Close',
  footer,
  groupBySection = true,
  ...state
}: ResumeComparisonStateProps & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: ReactNode;
  description?: ReactNode;
  closeLabel?: string;
  /** Extra actions rendered before the close button (e.g. "Undo", "Keep"). */
  footer?: ReactNode;
}) {
  const changeCount = state.changes.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden p-0">
        <DialogHeading
          title={title}
          description={
            description ?? (
              <>
                {changeCount === 0
                  ? 'Review what this change did to your resume.'
                  : `${changeCount} change${changeCount === 1 ? '' : 's'} were made to your resume.`}
              </>
            )
          }
        />

        <div className="flex-1 overflow-y-auto px-4 py-4 text-sm sm:px-6">
          <ResumeComparisonBody {...state} groupBySection={groupBySection} />
        </div>

        <DialogFooter className="gap-2 border-t border-border px-4 py-4 sm:px-6">
          {footer}
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {closeLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ResumeComparisonDialog;
